import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import Input from "../../components/form-item/Input";
import Modal from "../../components/modal/Modal";
import UploadProgressBar from "../../components/upload-progress/UploadProgressBar";
import { useUploadFailureDialog } from "../../components/upload-progress/useUploadFailureDialog";
import FileBrowser, {
  FileBrowserHandle,
} from "../../components/file-browser/FileBrowser";
import type { AudioInspectorBookmark } from "../../components/audio-preview/AudioInspectorBar";
import { FileBrowserShareAdapter } from "../../components/file-browser/FileBrowserShareModal";
import {
  FileBrowserFile,
  FileBrowserFolderDirectStats,
  FileBrowserFolderInfo,
  FileBrowserFolderNode,
  buildZipEntriesForFolder,
  parseFileSizeBytes,
} from "../../components/file-browser/fileBrowserUtils";
import useS3Upload, {
  type FileWithPath,
  getFolderPathFromRelativePath,
} from "../../hooks/useS3Upload";
import { useIsTwoColumn } from "../../hooks/useResponsiveLayout";
import useScrollDirectionLayoutSync from "../../hooks/useScrollDirectionLayoutSync";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { mapWithConcurrency } from "../../utils/concurrency";
import { buildLibraryLink, toMarkdownLink } from "../../utils/deepLink";
import { useGetProjectQuery } from "../../app/services/projects";
import {
  collectDescendantFolderIds,
  libraryApi,
  libraryFolderCacheKey,
  useGetLibraryTreeQuery,
  useGetLibrarySectionFilesQuery,
  useLazyGetLibrarySectionFilesQuery,
  useSearchLibraryFilesQuery,
  useLazyGetLibraryFolderFilesRecursiveQuery,
  useCreateLibraryFolderMutation,
  useUpdateLibraryFolderMutation,
  useDeleteLibraryFolderMutation,
  useRegisterLibraryFileMutation,
  useUpdateLibraryFileMutation,
  useDeleteLibraryFileMutation,
  useGetLibraryFileBookmarksQuery,
  useCreateLibraryBookmarkMutation,
  useUpdateLibraryBookmarkMutation,
  useDeleteLibraryBookmarkMutation,
  useLazyGetLibraryShareLinkQuery,
  useCreateLibraryShareLinkMutation,
  useDeleteLibraryShareLinkMutation,
  type LibraryFileItem,
  type LibraryFolderItem,
  type LibrarySection,
  type LibrarySectionBookmark,
  type LibraryShareTargetParams,
} from "../../app/services/library";
import LibrarySectionSettingsModal from "./components/LibrarySectionSettingsModal";
import LibraryBookmarkList from "./components/LibraryBookmarkList";
import LibrarySidebar, {
  type SidebarFolderAction,
} from "./components/LibrarySidebar";

/**
 * 共有ライブラリ（Library）ページ。
 *
 * 共有ノートと同じセクション型の権限モデル（閲覧 = 対象プロジェクトの
 * メンバーシップ / 編集 = ロール）で運用するファイルストレージ。
 *
 * 2 つの表示モードを持つ（Notes と同方針）:
 * - スタンドアロン（グローバルメニューの「共有ライブラリ」）: `/library`。
 *   参加権限のある全ライブラリを横断表示する。
 * - プロジェクト内埋め込み（`embedded`）: ProjectWrapper の library タブ
 *   （`/:projectId?tab=library`）としてストレージアイコンから開く。
 *   そのプロジェクトを対象にしたセクションのみ表示する。
 *
 * レイアウトも Notes と同じ 2 カラム構成:
 * - 左サイドバー（LibrarySidebar）: ライブラリ（セクション） > 階層フォルダの
 *   ツリー。フォルダを選択すると右の一覧がそのフォルダ配下のみに絞り込まれる。
 *   設定・フォルダ作成はセクション行の3点メニューから
 * - 右: 検索 + 汎用 FileBrowser（ツリー表示・D&D 移動・OS ドロップ
 *   アップロード・末尾「ファイルを追加」行・共有リンク・zip 一括DL）
 */

/**
 * ページ外枠。高さを確定させて自身は overflow: hidden とし、スクロールは
 * サイドバー / 本文それぞれの内側（overflow-y: auto）で独立して行う
 * （Notes / DM の 2 カラムと同じ構造）。
 * - スタンドアロン: GlobalHeader の表示状態（state.layout）に合わせて
 *   100dvh からヘッダー分を差し引く（GlobalWrapper の padding-top と連動）
 * - 埋め込み: ProjectWrapper の PanelTab（$fill）いっぱいに広がる
 * - 1 カラム時は MobileToolbar + （サイドバー or 本文）の縦積み
 */
const PageWrapper = styled.div<{
  $embedded: boolean;
  $singleColumn: boolean;
  $scrollDown: boolean;
}>`
  display: flex;
  flex-direction: ${({ $singleColumn }) => ($singleColumn ? "column" : "row")};
  width: 100%;
  height: ${({ $embedded, $scrollDown }) =>
    $embedded
      ? "100%"
      : $scrollDown
        ? "100dvh"
        : "calc(100dvh - var(--global-header-height))"};
  transition: height 0.15s linear;
  align-items: stretch;
  overflow: hidden;
`;

/** PC 用サイドバー外枠。幅はドラッグ境界（ResizeDivider）で可変 */
const SidebarPane = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
`;

/**
 * 1 カラム時にツールバー下の残り高さを占めるサイドバー用の枠。
 * 高さを確定させることで LibrarySidebar（height: 100% + overflow-y: auto）が
 * この中で独立してスクロールする。
 */
const SingleColumnSidebarPane = styled.div`
  flex: 1;
  min-height: 0;
`;

/** ドラッグ可能な境界線（Notes / Preview.tsx と同じパターン） */
const ResizeDivider = styled.div<{ $isDragging: boolean }>`
  width: 6px;
  flex-shrink: 0;
  cursor: col-resize;
  background: ${({ $isDragging }) =>
    $isDragging ? "var(--soremo-blue)" : "var(--soremo-border)"};
  transition: background 0.15s;
  position: relative;
  z-index: 5;

  &:hover {
    background: rgba(0, 154, 206, 0.4);
  }
`;

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;

/**
 * 本文エリア（右カラム）。ツールバーを固定し、テーブル部分だけが
 * 内側（Content）で独立してスクロールする。
 * $hidden: 1 カラムでサイドバー表示中は display: none で隠す。
 */
const ContentArea = styled.main<{ $hidden: boolean }>`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  flex-direction: column;
`;

/** 1 カラム時のみ描画するサイドバー開閉ツールバー（Notes と同じ） */
const MobileToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--soremo-border);
  flex-shrink: 0;
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--soremo-gray);
  padding: 4px;

  .material-symbols-rounded {
    font-size: 20px;
  }
`;

/**
 * ヘッダー（ツールバー + パンくず）。
 * $padRight: 埋め込み 2 カラム時はパネル右上にプロジェクトメニューのアイコンが
 * フロートするため（FloatingProjectMenu）、重ならないよう右に余白を取る
 * （1 カラム時は MobileToolbar 行がありアイコンはその右の空きに収まる）。
 */
const HeaderArea = styled.div<{ $padRight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  padding: 12px 16px 0;
  ${({ $padRight }) => ($padRight ? "padding-right: 70px;" : "")}
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 0;
`;

/** 選択中フォルダの階層パス（ライブラリ名 / フォルダ / …）。祖先はクリック可 */
const BreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-height: 24px;
  color: var(--soremo-light-gray);

  /* 祖先もリンク色にせず黒のまま（hover の背景でクリック可能と分かる） */
  .crumb-link {
    border: none;
    background: transparent;
    padding: 2px 4px;
    cursor: pointer;
    color: var(--soremo-gray);
    border-radius: 4px;

    @media (hover: hover) {
      &:hover {
        background: var(--soremo-background);
      }
    }
  }

  .crumb-current {
    padding: 2px 4px;
    color: var(--soremo-gray);
  }

  .crumb-sep {
    color: var(--soremo-border);
  }
`;

/**
 * 本文（テーブル）エリア。右カラム内で独立してスクロールする。
 * 表形式ファイルリストはテキストと違い幅制限せず常に全幅で使う。
 */
const Content = styled.div.attrs({
  // コンテンツ量の増減でスクロールバーが出入りしても幅が変わらないように
  className: "u-scrollbar-gutter-stable",
})`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 16px 120px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 96px 16px;
  text-align: center;
`;

/** アップロード進捗モーダルのタイトル（トークルーム / DM と同じ見た目） */
const UploadModalTitle = styled.h3.attrs({
  className: "heading-13",
})`
  margin: 0;
  text-align: center;
  color: var(--soremo-text);
`;

/**
 * S3 アップロード後の DB 登録（POST /api/library/files）の同時実行数。
 * 直列にすると大量アップロード時に登録だけで数十秒かかり、そのあいだに
 * タブを閉じられて取りこぼす事故につながるため並列で流す。
 */
const REGISTER_CONCURRENCY = 6;

/** S3 転送に成功したファイル 1 件ぶんの、DB 登録に必要な情報 */
interface PendingRegistration {
  /** 進捗行の ID。登録の成否をその行に反映するために持つ */
  fileId: string;
  fileKey: string;
  /** フォルダドロップ時のみ。BE が folder_id 起点にこの階層を作る */
  folderPath?: string;
}

/**
 * 登録を「フォルダパスごとの先頭 1 件」と「残り」に分ける。
 *
 * 同じ folder_path を同時に登録すると、BE が階層を作る過程で同名フォルダを
 * 重複生成しうる。先頭 1 件だけ直列で流して階層を先に作ってしまえば、
 * 残りは既存フォルダを参照するだけなので並列で流せる。
 */
const splitByFolderPath = (
  registrations: readonly PendingRegistration[],
): { heads: PendingRegistration[]; rest: PendingRegistration[] } => {
  const seen = new Set<string>();
  const heads: PendingRegistration[] = [];
  const rest: PendingRegistration[] = [];
  registrations.forEach((entry) => {
    const key = entry.folderPath ?? "";
    if (seen.has(key)) {
      rest.push(entry);
    } else {
      seen.add(key);
      heads.push(entry);
    }
  });
  return { heads, rest };
};

interface LibraryProps {
  /** ProjectWrapper の library タブ（`?tab=library`）としての埋め込み表示 */
  embedded?: boolean;
}

/**
 * サイドバーの3点メニューから保留中のフォルダ操作。
 * 操作対象がテーブル（FileBrowser）内に見えるスコープへ切り替えてから
 * （FileBrowser は key で remount されるため）モーダル等を開く必要があり、
 * 反映を待って effect で ref を叩く 2 段構えにする。
 * createFolder は「スコープ直下に作成」なので対象フォルダ ID を持たない。
 */
type PendingSidebarAction = {
  sectionId: string;
  /** 実行前に切り替えておくスコープ */
  scopeFolderId: string | null;
} & (
  | { action: "createFolder" }
  | {
      action: Exclude<SidebarFolderAction, "createFolder">;
      /** 操作対象のフォルダ ID */
      folderId: string;
    }
);

const Library: React.FC<LibraryProps> = ({ embedded = false }) => {
  const { projectId } = useParams<{ projectId: string }>();
  // 埋め込みは現在のプロジェクトを対象にしたセクションのみ表示する
  const projectFilter = embedded ? (projectId ?? null) : null;

  // レイアウト分岐（1 カラム ⇄ 2 カラム）はビューポート幅ベースの共通判定
  const isTwoColumn = useIsTwoColumn();
  // GlobalHeader の表示状態（"down" = 非表示）。スタンドアロン時の高さ計算用
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  // 内側スクロールから GlobalHeader / BottomAppBar の表示切替へ連動させる
  const contentScrollSync = useScrollDirectionLayoutSync<HTMLDivElement>();

  const {
    data: treeData,
    isLoading: treeLoading,
    error: treeError,
  } = useGetLibraryTreeQuery(
    projectFilter ? { project_id: projectFilter } : undefined,
  );

  const sections = useMemo(() => treeData?.data?.sections ?? [], [treeData]);
  const canManage = treeData?.data?.can_manage ?? false;

  // ── スコープ（サイドバーの選択状態） ──────────────────────────────
  // sectionId + folderId（null = セクションルート）。フォルダを選択すると
  // 右の一覧がそのフォルダ配下のみに絞り込まれる。

  const [scope, setScope] = useState<{
    sectionId: string;
    folderId: string | null;
  } | null>(null);

  const selectedSection: LibrarySection | null =
    (scope && sections.find((s) => s.id === scope.sectionId)) ??
    sections[0] ??
    null;

  // 選択フォルダが削除された場合などはセクションルートへフォールバック
  const scopeFolderId = useMemo(() => {
    if (!scope || !selectedSection || scope.sectionId !== selectedSection.id) {
      return null;
    }
    if (!scope.folderId) return null;
    return selectedSection.folders.some((f) => f.id === scope.folderId)
      ? scope.folderId
      : null;
  }, [scope, selectedSection]);

  const [searchText, setSearchText] = useState("");
  const [sectionModal, setSectionModal] = useState<
    { open: false } | { open: true; section: LibrarySection | null }
  >({ open: false });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // PC 用サイドバー幅（境界線ドラッグで可変）
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);

  // サイドバー各セクション先頭の「ブックマーク」ページ（null = 通常の一覧）
  const [bookmarksSectionId, setBookmarksSectionId] = useState<string | null>(
    null,
  );

  const selectScope = useCallback(
    (sectionId: string, folderId: string | null) => {
      setScope({ sectionId, folderId });
      // フォルダ / セクションを選んだら「ブックマーク」ページは抜ける
      setBookmarksSectionId(null);
      // 1 カラム時は選択したら本文（一覧）へ切り替える
      setMobileSidebarOpen(false);
    },
    [],
  );

  const selectBookmarksPage = useCallback((sectionId: string) => {
    setScope({ sectionId, folderId: null });
    setBookmarksSectionId(sectionId);
    setMobileSidebarOpen(false);
  }, []);

  // 境界線のドラッグリサイズ（Notes / Preview.tsx と同じ手動 col-resize パターン）
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingDivider(true);
      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const newWidth = startWidth + (ev.clientX - startX);
        setSidebarWidth(
          Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth)),
        );
      };

      const onMouseUp = () => {
        setIsDraggingDivider(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [sidebarWidth],
  );

  const fileBrowserRef = useRef<FileBrowserHandle>(null);

  const [pendingSidebarAction, setPendingSidebarAction] =
    useState<PendingSidebarAction | null>(null);

  // セクション行メニューの「フォルダを作成」（セクションルート直下）
  const handleSidebarCreateFolder = useCallback(
    (section: LibrarySection) => {
      selectScope(section.id, null);
      setPendingSidebarAction({
        action: "createFolder",
        sectionId: section.id,
        scopeFolderId: null,
      });
    },
    [selectScope],
  );

  // フォルダ行の3点メニュー（ファイルリスト側のフォルダメニューと同じ機能）
  const handleSidebarFolderAction = useCallback(
    (
      section: LibrarySection,
      folder: LibraryFolderItem,
      action: SidebarFolderAction,
    ) => {
      if (action === "createFolder") {
        // 対象フォルダ自身をスコープにして、その直下に作成する
        selectScope(section.id, folder.id);
        setPendingSidebarAction({
          action,
          sectionId: section.id,
          scopeFolderId: folder.id,
        });
        return;
      }
      // moveFolderToRoot は移動結果が見えるセクションルートへ、
      // それ以外は対象の行が見える親フォルダへスコープを切り替える
      const scopeId = action === "moveFolderToRoot" ? null : folder.parent_id;
      selectScope(section.id, scopeId);
      setPendingSidebarAction({
        action,
        sectionId: section.id,
        scopeFolderId: scopeId,
        folderId: folder.id,
      });
    },
    [selectScope],
  );

  // スコープ切り替えの反映（対象 FileBrowser の mount）を待ってから実行する
  useEffect(() => {
    const pending = pendingSidebarAction;
    if (!pending) return;
    if (selectedSection?.id !== pending.sectionId) return;
    if (scopeFolderId !== pending.scopeFolderId) return;
    const handle = fileBrowserRef.current;
    if (!handle) return;
    setPendingSidebarAction(null);
    if (pending.action === "createFolder") {
      // サイドバー経由の作成先は常にスコープ直下（= FileBrowser のルート）
      handle.openCreateFolder(null);
      return;
    }
    const { folderId } = pending;
    switch (pending.action) {
      case "renameFolder":
        handle.openRenameFolder(folderId);
        break;
      case "deleteFolder":
        handle.openDeleteFolder(folderId);
        break;
      case "shareFolder":
        handle.openShareFolder(folderId);
        break;
      case "downloadFolder":
        handle.downloadFolder(folderId);
        break;
      case "transcribeFolder":
        handle.transcribeFolder(folderId);
        break;
      case "moveFolderToRoot":
        handle.moveFolderToRoot(folderId);
        break;
    }
  }, [pendingSidebarAction, selectedSection?.id, scopeFolderId]);

  // ── API ──────────────────────────────────────────────────────────

  const dispatch = useDispatch();
  const [registerFile] = useRegisterLibraryFileMutation();
  const [updateFile] = useUpdateLibraryFileMutation();
  const [deleteFile] = useDeleteLibraryFileMutation();
  const [createFolder] = useCreateLibraryFolderMutation();
  const [updateFolder] = useUpdateLibraryFolderMutation();
  const [deleteFolder] = useDeleteLibraryFolderMutation();
  const [fetchShareLink] = useLazyGetLibraryShareLinkQuery();
  const [createShareLink] = useCreateLibraryShareLinkMutation();
  const [deleteShareLink] = useDeleteLibraryShareLinkMutation();

  // S3 アップロード（DB 登録は専用 API POST /api/library/files で行う）
  const {
    uploadFiles: uploadFilesToS3,
    uploadingFiles,
    abortFile,
    clearCompleted,
    clearFailed,
    retryFile,
    retryFiles,
    postProcessLabel,
    setPostStatus,
    beginPostProcess,
    endPostProcess,
    withPostProcess,
  } = useS3Upload({
    purpose: "shared_library",
    sectionId: selectedSection?.id,
    skipRegister: true,
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // アップロード完了時に自動で進捗モーダルを閉じる（トークルームと同じ挙動）。
  // エラー行が残っている場合は自動クリアしない（失敗ダイアログの選択で解決する）。
  // 登録に失敗した行も、ユーザーが気づけるよう自動では畳まない。
  useEffect(() => {
    if (uploadingFiles.length === 0) return;
    const allDone = uploadingFiles.every(
      (f) => f.status === "completed" && f.postStatus !== "error",
    );
    if (allDone) {
      // 少し遅延させてユーザーに完了を見せてからクリア
      const timer = setTimeout(() => {
        clearCompleted();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  // 共有リンクの有効期限の初期値: プロジェクトの開発完了日（埋め込み時のみ）
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectFilter ?? "" },
    { skip: !projectFilter },
  );
  const projectEndDate =
    projectData?.data?.project?.end_date?.slice(0, 10) || null;

  // ── ファイルの遅延取得（フォルダ単位 → セクション別の蓄積キャッシュ） ──
  // ツリー API はフォルダ構造 + 集計のみでファイルを含まないため、
  // ファイルは「スコープ（選択フォルダ）直下」を購読し、フォルダ展開の
  // たびに追加取得してセクション単位の蓄積キャッシュへ merge していく。

  const {
    // data ではなく currentData: セクション切替（キャッシュキー変更）直後に
    // 前セクションの蓄積ファイルが一瞬表示されるのを防ぐ。同一セクション内の
    // スコープ変更ではキーが変わらないため、蓄積キャッシュが出続ける。
    currentData: filesCache,
    error: filesError,
    isFetching: filesFetching,
  } = useGetLibrarySectionFilesQuery(
    {
      section_id: selectedSection?.id ?? "",
      folder_id: scopeFolderId,
    },
    { skip: !selectedSection },
  );

  // ロード済みフォルダキー（"root" またはフォルダ ID）
  const loadedFolderKeys = useMemo(
    () => new Set(filesCache?.loadedFolderKeys ?? []),
    [filesCache],
  );
  const scopeFilesLoaded = loadedFolderKeys.has(
    libraryFolderCacheKey(scopeFolderId),
  );

  // フォルダ展開などで要求された未ロードフォルダのキュー。
  // 蓄積キャッシュは serializeQueryArgs でセクション単位の 1 エントリの
  // ため、in-flight 中に別フォルダを要求しても fetch が始まらない
  // （RTK Query が同一キャッシュキーへ dedupe する）。完了を待って
  // 1 件ずつ直列に流す。
  const [triggerSectionFiles] = useLazyGetLibrarySectionFilesQuery();
  const [folderLoadQueue, setFolderLoadQueue] = useState<string[]>([]);
  const inflightFolderRef = useRef<string | null>(null);

  // セクションを切り替えたら前セクションの要求は破棄する
  useEffect(() => {
    setFolderLoadQueue([]);
    inflightFolderRef.current = null;
  }, [selectedSection?.id]);

  const requestFolderLoad = useCallback((folderId: string) => {
    setFolderLoadQueue((prev) =>
      prev.includes(folderId) ? prev : [...prev, folderId],
    );
  }, []);

  const queueHeadFolderId = folderLoadQueue[0] ?? null;
  useEffect(() => {
    if (!selectedSection || !queueHeadFolderId) return;
    // ロード済み（merge 完了 or もともとロード済み）ならキューを進める
    if (loadedFolderKeys.has(libraryFolderCacheKey(queueHeadFolderId))) {
      inflightFolderRef.current = null;
      setFolderLoadQueue((prev) =>
        prev.filter((id) => id !== queueHeadFolderId),
      );
      return;
    }
    // スコープ側 or 前の要求の fetch 中は完了を待つ（dedupe 対策）
    if (filesFetching) return;
    if (inflightFolderRef.current === queueHeadFolderId) return;
    inflightFolderRef.current = queueHeadFolderId;
    triggerSectionFiles({
      section_id: selectedSection.id,
      folder_id: queueHeadFolderId,
    })
      .unwrap()
      .catch(() => {
        // 失敗したフォルダはキューから外す（再展開で再試行できる）
        toast.error("フォルダ内のファイルを読み込めませんでした");
        inflightFolderRef.current = null;
        setFolderLoadQueue((prev) =>
          prev.filter((id) => id !== queueHeadFolderId),
        );
      });
  }, [
    selectedSection,
    queueHeadFolderId,
    loadedFolderKeys,
    filesFetching,
    triggerSectionFiles,
  ]);

  // ── サーバー検索（スコープ配下を再帰・部分一致） ──────────────────
  // 蓄積キャッシュにはロード済みフォルダのファイルしか無いため、検索は
  // クライアントフィルタではなくサーバーへ投げる。
  const debouncedSearchText = useDebouncedValue(searchText.trim(), 300);
  const searchActive = debouncedSearchText.length > 0;
  const { data: searchResults, isFetching: searchFetching } =
    useSearchLibraryFilesQuery(
      {
        section_id: selectedSection?.id ?? "",
        folder_id: scopeFolderId,
        q: debouncedSearchText,
      },
      { skip: !selectedSection || !searchActive },
    );

  // ── FileBrowser 用データ（スコープで絞り込み） ────────────────────
  // フォルダ選択中は、その配下だけを切り出して「選択フォルダ直下 =
  // 見かけ上のルート（parentId / folderId = null）」に写像して渡す。
  // FileBrowser から返る folderId = null は選択フォルダへ読み替える
  // （resolveFolderId）。

  // 選択フォルダ配下の全フォルダ ID（自身は含まない）
  const scopedDescendantIds = useMemo(() => {
    if (!selectedSection || !scopeFolderId) return null;
    const ids = collectDescendantFolderIds(
      selectedSection.folders,
      scopeFolderId,
    );
    ids.delete(scopeFolderId);
    return ids;
  }, [selectedSection, scopeFolderId]);

  const canEditSection = selectedSection?.can_edit ?? false;
  const toBrowserFile = useCallback(
    (f: LibraryFileItem): FileBrowserFile => ({
      id: f.id,
      name: f.file_name,
      fileType: f.file_type,
      originalUrl: f.original_url ?? "",
      optimizedUrl: f.optimized_url,
      waveformUrl: f.has_waveform ? f.waveform_url : null,
      sizeBytes: parseFileSizeBytes(f.file_metadata),
      updatedAt: f.updated_at,
      folderId: f.folder_id,
      isShared: f.is_shared,
      bookmarkCount: f.bookmark_count,
      bookmarkUsers: f.bookmark_users.map((u) => ({
        id: u.id,
        name: u.display_name,
        avatar: u.avatar,
      })),
      canEdit: canEditSection,
    }),
    [canEditSection],
  );

  const files = useMemo<FileBrowserFile[]>(() => {
    if (!selectedSection) return [];
    // 遅延ロード済みキャッシュをスコープで絞り、選択フォルダ直下を
    // 見かけ上のルート（folderId = null）へ写像する
    const all = (filesCache?.files ?? []).map(toBrowserFile);
    const scopedCache =
      !scopeFolderId || !scopedDescendantIds
        ? all
        : all
            .filter(
              (f) =>
                f.folderId === scopeFolderId ||
                (f.folderId !== null && scopedDescendantIds.has(f.folderId)),
            )
            .map((f) =>
              f.folderId === scopeFolderId ? { ...f, folderId: null } : f,
            );
    if (!searchActive) return scopedCache;
    // 検索中: サーバー検索の結果（スコープ配下の再帰検索のため descendant
    // フィルタは不要。folderId の写像だけ行う）に、ロード済みキャッシュも
    // 合わせて渡す。検索でヒットしたフォルダを展開したとき、配下ファイル
    // （検索語に一致しないものを含む）をツリー経由で表示するため。
    // 同一 ID はサーバー検索の結果を優先する。
    const merged = new Map<string, FileBrowserFile>();
    scopedCache.forEach((f) => merged.set(f.id, f));
    (searchResults ?? []).forEach((f) => {
      const file = toBrowserFile(f);
      merged.set(
        file.id,
        scopeFolderId && file.folderId === scopeFolderId
          ? { ...file, folderId: null }
          : file,
      );
    });
    return [...merged.values()];
  }, [
    selectedSection,
    searchActive,
    searchResults,
    filesCache,
    toBrowserFile,
    scopeFolderId,
    scopedDescendantIds,
  ]);

  const folders = useMemo<FileBrowserFolderInfo[]>(() => {
    if (!selectedSection) return [];
    const all = selectedSection.folders.map((f) => ({
      id: f.id,
      name: f.name,
      parentId: f.parent_id,
      updatedAt: f.updated_at,
      isShared: f.is_shared,
      canEdit: selectedSection.can_edit,
    }));
    if (!scopeFolderId || !scopedDescendantIds) return all;
    return all
      .filter((f) => scopedDescendantIds.has(f.id))
      .map((f) =>
        f.parentId === scopeFolderId ? { ...f, parentId: null } : f,
      );
  }, [selectedSection, scopeFolderId, scopedDescendantIds]);

  // フォルダ直下のファイル集計（ツリー API 提供）。手元の files は
  // ロード済みフォルダの分しか無いため、フォルダ行の件数・サイズ・
  // 最終更新はこの直下集計 + 子フォルダの畳み込みで計算する。
  const folderStats = useMemo(() => {
    const map = new Map<string, FileBrowserFolderDirectStats>();
    selectedSection?.folder_stats.forEach((s) => {
      // folder_id = null（セクションルート直下分）はフォルダ行にならない
      if (!s.folder_id) return;
      map.set(s.folder_id, {
        fileCount: s.file_count,
        totalSize: s.total_size,
        latestUpdated: s.latest_file_at,
      });
    });
    return map;
  }, [selectedSection]);

  /**
   * FileBrowser の「ルート」（folderId = null）をスコープへ読み替える。
   * フォルダ選択中のルートは選択フォルダ自身を指す。
   */
  const resolveFolderId = useCallback(
    (folderId: string | null): string | null => folderId ?? scopeFolderId,
    [scopeFolderId],
  );

  // ── zip 一括ダウンロード（配下全件を API で解決） ────────────────
  // 手元にはロード済みフォルダのファイルしか無いため、対象フォルダ配下の
  // 全ファイルを recursive API で取得してから zip エントリを組み立てる。

  const [fetchFolderFilesRecursive] = useLazyGetLibraryFolderFilesRecursiveQuery();

  // zip の階層パス復元用の全フォルダ（スコープ写像を通さない実構造）
  const allFolderInfos = useMemo<FileBrowserFolderInfo[]>(
    () =>
      (selectedSection?.folders ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        parentId: f.parent_id,
      })),
    [selectedSection],
  );

  const resolveZipEntries = useCallback(
    async (node: FileBrowserFolderNode) => {
      if (!selectedSection) return [];
      const recursiveFiles = await fetchFolderFilesRecursive({
        section_id: selectedSection.id,
        folder_id: node.id,
      }).unwrap();
      return buildZipEntriesForFolder(
        recursiveFiles.map(toBrowserFile),
        allFolderInfos,
        node.id,
      );
    },
    [
      selectedSection,
      fetchFolderFilesRecursive,
      toBrowserFile,
      allFolderInfos,
    ],
  );

  /**
   * 一括文字起こしの対象を解決する（遅延ロード対応）。
   * zip 用の resolveZipEntries と同じ「配下全件取得 API」を使うが、
   * 音声・動画の判定に fileType が要るので FileBrowserFile のまま返す。
   */
  const resolveFolderFiles = useCallback(
    async (node: FileBrowserFolderNode) => {
      if (!selectedSection) return [];
      const recursiveFiles = await fetchFolderFilesRecursive({
        section_id: selectedSection.id,
        folder_id: node.id,
      }).unwrap();
      return recursiveFiles.map(toBrowserFile);
    },
    [selectedSection, fetchFolderFilesRecursive, toBrowserFile],
  );

  // 選択フォルダの階層パス（パンくず用。ルート → 選択フォルダの順）
  const scopePath = useMemo<LibraryFolderItem[]>(() => {
    if (!selectedSection || !scopeFolderId) return [];
    const byId = new Map(selectedSection.folders.map((f) => [f.id, f]));
    const path: LibraryFolderItem[] = [];
    let current = byId.get(scopeFolderId);
    while (current) {
      path.unshift(current);
      current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    return path;
  }, [selectedSection, scopeFolderId]);

  // ── ファイル操作 ─────────────────────────────────────────────────

  const handleUploadFiles = useCallback(
    async (entries: FileWithPath[], folderId: string | null) => {
      if (!selectedSection) return;
      const targetFolderId = resolveFolderId(folderId);
      // 進捗も結果も進捗モーダルの各ファイル行で表示する。
      // トーストは、行が 1 つも出ない異常系だけに使う。
      try {
        // システムファイル（.DS_Store 等）は uploadFilesToS3 側で除外されるため、
        // 件数は戻り値ベースで数える（フォルダドロップ時に混入しやすい）
        const results = await uploadFilesToS3(entries);
        if (results.length === 0) {
          toast.error("アップロードできるファイルがありませんでした");
          return;
        }
        // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
        const hadFailures = results.some((r) => r.status === "error");
        const resolved = await resolveUploadFailures(results);
        if (resolved === null) {
          // キャンセル: 進捗を畳んで中断（DB 登録はしない）
          clearCompleted();
          return;
        }
        // 失敗分だけ畳む（成功分の行は登録状況を出すために残す）
        if (hadFailures) clearFailed();
        // フォルダごとのアップロードは relativePath から階層を復元する
        // （BE が folder_id 起点に folder_path のフォルダを作成する）
        const registrations: PendingRegistration[] = resolved
          .filter((uploaded) => uploaded.fileKey)
          .map((uploaded) => ({
            fileId: uploaded.id,
            fileKey: uploaded.fileKey!,
            folderPath: uploaded.relativePath
              ? getFolderPathFromRelativePath(uploaded.relativePath)
              : undefined,
          }));
        const { heads, rest } = splitByFolderPath(registrations);

        const registerOne = async (entry: PendingRegistration) => {
          setPostStatus(entry.fileId, "running");
          try {
            await registerFile({
              section_id: selectedSection.id,
              file_key: entry.fileKey,
              ...(targetFolderId ? { folder_id: targetFolderId } : {}),
              ...(entry.folderPath ? { folder_path: entry.folderPath } : {}),
              // 1 件ごとにツリーを取り直すと件数が少しずつ増える表示になるため、
              // 全件終わってからまとめて再取得する
              defer_invalidate: true,
            }).unwrap();
            setPostStatus(entry.fileId, "done");
          } catch {
            // 個別失敗はその行にだけ出して、残りの登録は続行する
            setPostStatus(entry.fileId, "error", "登録に失敗しました");
          }
        };

        // S3 転送が終わっても登録が残っているあいだは「処理中」として扱う
        // （進捗モーダルを閉じさせず、タブを閉じようとしたら引き止める）
        beginPostProcess("登録中");
        registrations.forEach((entry) => setPostStatus(entry.fileId, "waiting"));
        try {
          for (const entry of heads) {
            await registerOne(entry);
          }
          await mapWithConcurrency(rest, REGISTER_CONCURRENCY, registerOne);
        } finally {
          dispatch(
            libraryApi.util.invalidateTags([
              { type: "LibraryTree", id: "TREE" },
            ]),
          );
          endPostProcess();
        }
      } catch {
        toast.error("アップロードに失敗しました");
      }
    },
    [
      selectedSection,
      resolveFolderId,
      uploadFilesToS3,
      resolveUploadFailures,
      clearCompleted,
      clearFailed,
      registerFile,
      beginPostProcess,
      endPostProcess,
      setPostStatus,
      dispatch,
    ],
  );

  const handleRenameFile = useCallback(
    async (file: FileBrowserFile, newName: string) => {
      await updateFile({ file_id: file.id, file_name: newName }).unwrap();
    },
    [updateFile],
  );

  const handleMoveFile = useCallback(
    async (file: FileBrowserFile, folderId: string | null) => {
      await updateFile({
        file_id: file.id,
        folder_id: resolveFolderId(folderId),
      }).unwrap();
    },
    [updateFile, resolveFolderId],
  );

  const handleReplaceFile = useCallback(
    async (file: FileBrowserFile, nativeFile: File) => {
      const results = await uploadFilesToS3([nativeFile]);
      // 失敗時は「再試行 / キャンセル」ダイアログで解決（1件のため続行選択肢なし）
      const hadFailures = results.some((r) => r.status === "error");
      const resolved = await resolveUploadFailures(results);
      if (resolved === null || hadFailures) clearCompleted();
      const uploaded = resolved?.find((r) => r.fileKey);
      if (!uploaded?.fileKey) {
        throw new Error("upload failed");
      }
      await withPostProcess(
        () =>
          updateFile({
            file_id: file.id,
            file_key: uploaded.fileKey!,
          }).unwrap(),
        "ファイルを差し替え中",
      );
    },
    [
      uploadFilesToS3,
      resolveUploadFailures,
      clearCompleted,
      updateFile,
      withPostProcess,
    ],
  );

  const handleDeleteFile = useCallback(
    async (file: FileBrowserFile) => {
      await deleteFile({ file_id: file.id }).unwrap();
    },
    [deleteFile],
  );

  // ── フォルダ操作 ─────────────────────────────────────────────────

  const handleCreateFolder = useCallback(
    async (name: string, parentId: string | null) => {
      if (!selectedSection) return;
      const targetParentId = resolveFolderId(parentId);
      const res = await createFolder({
        section_id: selectedSection.id,
        name,
        ...(targetParentId ? { parent_id: targetParentId } : {}),
      }).unwrap();
      // FileBrowser が作成行へのスクロール + ハイライトに使う
      return res.data.folder.id;
    },
    [selectedSection, resolveFolderId, createFolder],
  );

  const handleRenameFolder = useCallback(
    async (node: FileBrowserFolderNode, newName: string) => {
      await updateFolder({ folder_id: node.id, name: newName }).unwrap();
    },
    [updateFolder],
  );

  const handleMoveFolder = useCallback(
    async (node: FileBrowserFolderNode, parentId: string | null) => {
      await updateFolder({
        folder_id: node.id,
        parent_id: resolveFolderId(parentId),
      }).unwrap();
    },
    [updateFolder, resolveFolderId],
  );

  const handleDeleteFolder = useCallback(
    async (node: FileBrowserFolderNode) => {
      await deleteFolder({ folder_id: node.id }).unwrap();
    },
    [deleteFolder],
  );

  // ── オーディオブックマーク（インスペクタ表示中のファイルのみ取得） ──

  const [inspectorFileId, setInspectorFileId] = useState<string | null>(null);
  const { data: bookmarksData } = useGetLibraryFileBookmarksQuery(
    { file_id: inspectorFileId ?? "" },
    { skip: !inspectorFileId },
  );
  const [createBookmark] = useCreateLibraryBookmarkMutation();
  const [updateBookmark] = useUpdateLibraryBookmarkMutation();
  const [deleteBookmark] = useDeleteLibraryBookmarkMutation();

  const audioBookmarks = useMemo<AudioInspectorBookmark[]>(() => {
    if (!bookmarksData) return [];
    return bookmarksData.data.bookmarks.map((b) => ({
      id: b.id,
      startSeconds: b.start_seconds,
      endSeconds: b.end_seconds,
      label: b.label,
      createdByName: b.created_by?.display_name ?? null,
      createdByAvatar: b.created_by?.avatar ?? null,
    }));
  }, [bookmarksData]);

  const handleCreateAudioBookmark = useCallback(
    async (
      fileId: string,
      params: { startSeconds: number; endSeconds: number | null },
    ) => {
      try {
        // 追加トーストは出さない（波形ピン / 一覧のアバターで分かるため）
        await createBookmark({
          file_id: fileId,
          start_seconds: params.startSeconds,
          end_seconds: params.endSeconds,
        }).unwrap();
      } catch {
        toast.error("ブックマークを追加できませんでした");
      }
    },
    [createBookmark],
  );

  const handleUpdateAudioBookmark = useCallback(
    async (bookmarkId: string, params: { label: string }) => {
      if (!inspectorFileId) return;
      try {
        await updateBookmark({
          bookmark_id: bookmarkId,
          file_id: inspectorFileId,
          label: params.label,
        }).unwrap();
      } catch {
        toast.error("ブックマークを更新できませんでした");
      }
    },
    [updateBookmark, inspectorFileId],
  );

  const handleDeleteAudioBookmark = useCallback(
    async (bookmarkId: string) => {
      if (!inspectorFileId) return;
      try {
        await deleteBookmark({
          bookmark_id: bookmarkId,
          file_id: inspectorFileId,
        }).unwrap();
      } catch {
        toast.error("ブックマークを削除できませんでした");
      }
    },
    [deleteBookmark, inspectorFileId],
  );

  // 「ブックマーク」ページの行クリック → 該当ファイルの場所へスコープを
  // 切り替え、FileBrowser 経由でインスペクタを開いて頭出しする指示。
  // requestId は適用済み判定用（同じブックマークを連続で押しても動くように
  // 毎回インクリメントする）。
  const [audioJump, setAudioJump] = useState<
    | {
        fileId: string;
        requestId: number;
        startSeconds: number;
        endSeconds: number | null;
      }
    | null
  >(null);
  const jumpCounterRef = useRef(0);

  const handleBookmarkListJump = useCallback(
    (bookmark: LibrarySectionBookmark) => {
      if (!selectedSection) return;
      jumpCounterRef.current += 1;
      setBookmarksSectionId(null);
      // ファイルの場所（フォルダ）をスコープにして対象行を表示する。
      // フォルダが見えない（削除等）場合は scopeFolderId 側でルートへ
      // フォールバックする。
      setScope({
        sectionId: selectedSection.id,
        folderId: bookmark.file.folder_id,
      });
      setAudioJump({
        fileId: bookmark.file.id,
        requestId: jumpCounterRef.current,
        startSeconds: bookmark.start_seconds,
        endSeconds: bookmark.end_seconds,
      });
    },
    [selectedSection],
  );

  const handleAudioFocusApplied = useCallback(() => setAudioJump(null), []);

  // ── ディープリンク ────────────────────────────────────────────────
  // `?section={id}&folder={id}&file={id}` を読み、スコープ（セクション +
  // フォルダ）を復元する。file 指定が音源なら既存の audioJump 経路で
  // インスペクタを開いて頭出しする（音源以外はフォルダ表示のみ）。
  // 適用後はパラメータを外し、以降の操作や再訪と衝突させない。

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const sectionParam = searchParams.get("section");
    if (!sectionParam) return;
    // ツリー取得を待ってから解決する
    if (treeLoading) return;
    deepLinkAppliedRef.current = true;

    const targetSection = sections.find((s) => s.id === sectionParam);
    if (targetSection) {
      const folderParam = searchParams.get("folder");
      const fileParam = searchParams.get("file");
      setScope({ sectionId: targetSection.id, folderId: folderParam });
      setBookmarksSectionId(null);
      setMobileSidebarOpen(false);
      if (fileParam) {
        jumpCounterRef.current += 1;
        setAudioJump({
          fileId: fileParam,
          requestId: jumpCounterRef.current,
          startSeconds: 0,
          endSeconds: null,
        });
      }
    } else {
      toast.error("リンク先のライブラリが見つからないか、閲覧権限がありません");
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("section");
        next.delete("folder");
        next.delete("file");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, sections, treeLoading, setSearchParams]);

  // 行メニュー「リンクをコピー」: Markdown 形式（[名前](URL)）で発行する。
  // 埋め込み時はプロジェクトスコープ URL（?tab=library&…）、スタンドアロン時は
  // /library?… を使う。
  const { copy: copyDeepLink } = useCopyToClipboard({
    successMessage: "リンクをコピーしました",
  });
  const handleCopyLink = useCallback(
    (target: {
      type: "file" | "folder";
      id: string;
      name: string;
      folderId: string | null;
    }) => {
      if (!selectedSection) return;
      const url = buildLibraryLink(
        {
          sectionId: selectedSection.id,
          folderId: target.folderId,
          fileId: target.type === "file" ? target.id : undefined,
        },
        embedded ? (projectId ?? undefined) : undefined,
      );
      void copyDeepLink(toMarkdownLink(target.name, url));
    },
    [selectedSection, embedded, projectId, copyDeepLink],
  );

  // 「ブックマーク」ページを表示中か（対象セクションが見えている間だけ有効）
  const bookmarksViewActive =
    bookmarksSectionId !== null && selectedSection?.id === bookmarksSectionId;

  // ── 共有リンク ───────────────────────────────────────────────────

  const shareAdapter = useMemo<FileBrowserShareAdapter | undefined>(() => {
    // 共有リンクの管理は editor 以上（BE でも同条件で検証する）
    if (!selectedSection?.can_edit) return undefined;
    const toParams = (target: {
      type: "file" | "folder";
      id: string;
    }): LibraryShareTargetParams =>
      target.type === "file"
        ? { file_id: target.id }
        : { folder_id: target.id };
    return {
      getLink: async (target) => {
        const result = await fetchShareLink(toParams(target)).unwrap();
        return result.data;
      },
      createLink: async (target, settings) => {
        const result = await createShareLink({
          ...toParams(target),
          expires_at: settings.expires_at,
          require_membership: settings.require_membership,
        }).unwrap();
        return result.data;
      },
      deleteLink: async (target) => {
        await deleteShareLink(toParams(target)).unwrap();
      },
      defaultExpiryDate: projectEndDate,
    };
  }, [
    selectedSection?.can_edit,
    fetchShareLink,
    createShareLink,
    deleteShareLink,
    projectEndDate,
  ]);

  // ── 描画 ─────────────────────────────────────────────────────────

  if (treeLoading) return <SvgLoadingAnimation />;

  if (treeError) {
    return (
      <EmptyState>
        <p className="bodytext-13 u-text-light-gray">
          ライブラリの読み込みに失敗しました
        </p>
      </EmptyState>
    );
  }

  // 閲覧できるセクションが無い場合（master_admin には作成導線を出す）
  if (sections.length === 0) {
    return (
      <PageWrapper
        $embedded={embedded}
        $singleColumn={!isTwoColumn}
        $scrollDown={scrollState === "down"}
      >
        <EmptyState style={{ flex: 1 }}>
          <span
            className="material-symbols-rounded"
            style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}
          >
            home_storage
          </span>
          <p className="bodytext-13 u-text-light-gray">
            {canManage
              ? "「セクションを追加」から共有ライブラリを作成できます"
              : "閲覧できる共有ライブラリはまだありません"}
          </p>
          {canManage && (
            <Button
              size="small"
              leftIcon="add"
              onClick={() => setSectionModal({ open: true, section: null })}
            >
              セクションを追加
            </Button>
          )}
        </EmptyState>
        {sectionModal.open && (
          <LibrarySectionSettingsModal
            section={sectionModal.section}
            canManage={canManage}
            onClose={() => setSectionModal({ open: false })}
          />
        )}
      </PageWrapper>
    );
  }

  const canEdit = selectedSection?.can_edit ?? false;

  const sidebar = (
    <LibrarySidebar
      sections={sections}
      selectedSectionId={selectedSection?.id ?? null}
      selectedFolderId={scopeFolderId}
      bookmarksSectionId={bookmarksViewActive ? bookmarksSectionId : null}
      canManage={canManage}
      // グローバルメニューの「共有ライブラリ」で複数ライブラリが並ぶときは、
      // まだ開閉していないライブラリを閉じて一覧性を優先する
      defaultSectionsCollapsed={!embedded && sections.length > 1}
      onSelect={selectScope}
      onSelectBookmarks={selectBookmarksPage}
      onOpenSectionSettings={(section) =>
        setSectionModal({ open: true, section })
      }
      onCreateFolder={handleSidebarCreateFolder}
      onFolderAction={handleSidebarFolderAction}
      onCreateSection={() => setSectionModal({ open: true, section: null })}
    />
  );

  return (
    <PageWrapper
      $embedded={embedded}
      $singleColumn={!isTwoColumn}
      $scrollDown={scrollState === "down"}
    >
      {!isTwoColumn && (
        <MobileToolbar>
          <MobileMenuButton
            onClick={() => setMobileSidebarOpen((v) => !v)}
            aria-label="ライブラリ一覧を開閉"
          >
            <span className="material-symbols-rounded">
              {mobileSidebarOpen ? "left_panel_close" : "left_panel_open"}
            </span>
            <span className="bodytext-13">ライブラリ一覧</span>
          </MobileMenuButton>
        </MobileToolbar>
      )}

      {isTwoColumn ? (
        // 2 カラム: 境界線ドラッグで幅を変更
        <>
          <SidebarPane $width={sidebarWidth}>{sidebar}</SidebarPane>
          <ResizeDivider
            $isDragging={isDraggingDivider}
            onMouseDown={handleDividerMouseDown}
          />
        </>
      ) : (
        // 1 カラム: ツールバーのトグルでサイドバー（一覧）と本文を排他表示
        mobileSidebarOpen && (
          <SingleColumnSidebarPane>{sidebar}</SingleColumnSidebarPane>
        )
      )}

      <ContentArea $hidden={!isTwoColumn && mobileSidebarOpen}>
        <HeaderArea $padRight={embedded && isTwoColumn}>
          {/* アップロードは FileBrowser 末尾の「ファイルを追加」行と D&D に統一。
              フォルダ作成・設定はサイドバーのセクション行メニューから */}
          {!bookmarksViewActive && (
            <ToolbarRow>
              <SearchBox>
                <Input
                  type="search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="ファイル名・フォルダ名で検索..."
                />
              </SearchBox>
            </ToolbarRow>
          )}

          {/* 「ブックマーク」ページのパンくず（セクション名クリックで一覧へ戻る） */}
          {bookmarksViewActive && selectedSection && (
            <BreadcrumbRow className="bodytext-13">
              <button
                type="button"
                className="crumb-link"
                onClick={() => selectScope(selectedSection.id, null)}
              >
                {selectedSection.title}
              </button>
              <span className="crumb-sep">/</span>
              <span className="crumb-current">ブックマーク</span>
            </BreadcrumbRow>
          )}

          {/* フォルダ絞り込み中のパンくず（祖先クリックでそのスコープへ） */}
          {!bookmarksViewActive && selectedSection && scopePath.length > 0 && (
            <BreadcrumbRow className="bodytext-13">
              <button
                type="button"
                className="crumb-link"
                onClick={() => selectScope(selectedSection.id, null)}
              >
                {selectedSection.title}
              </button>
              {scopePath.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <span className="crumb-sep">/</span>
                  {index === scopePath.length - 1 ? (
                    <span className="crumb-current">{folder.name}</span>
                  ) : (
                    <button
                      type="button"
                      className="crumb-link"
                      onClick={() => selectScope(selectedSection.id, folder.id)}
                    >
                      {folder.name}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </BreadcrumbRow>
          )}
        </HeaderArea>

        <Content {...contentScrollSync}>
          {/* 「ブックマーク」ページ（セクション内の全ブックマーク一覧） */}
          {bookmarksViewActive && selectedSection && (
            <LibraryBookmarkList
              section={selectedSection}
              onJump={handleBookmarkListJump}
              audioInspectorBottomOffset={embedded ? 80 : 0}
            />
          )}
          {!bookmarksViewActive && selectedSection && (
            <FileBrowser
              key={`${selectedSection.id}:${scopeFolderId ?? "root"}`}
              ref={fileBrowserRef}
              files={files}
              folders={folders}
              searchText={searchText}
              // スコープ直下の初回ロード中 / サーバー検索中のローディング行
              isLoading={
                searchActive
                  ? searchFetching
                  : !scopeFilesLoaded && !filesError
              }
              folderStats={folderStats}
              loadedFolderIds={loadedFolderKeys}
              onExpandFolder={requestFolderLoad}
              resolveZipEntries={resolveZipEntries}
              enableTranscription
              resolveFolderFiles={resolveFolderFiles}
              canEditRoot={canEdit}
              emptyMessage={
                scopeFolderId
                  ? "このフォルダにはまだファイルがありません"
                  : "まだファイルがありません"
              }
              onRenameFile={canEdit ? handleRenameFile : undefined}
              onMoveFile={canEdit ? handleMoveFile : undefined}
              onReplaceFile={canEdit ? handleReplaceFile : undefined}
              onDeleteFile={canEdit ? handleDeleteFile : undefined}
              onUploadFiles={canEdit ? handleUploadFiles : undefined}
              onCreateFolder={canEdit ? handleCreateFolder : undefined}
              onRenameFolder={canEdit ? handleRenameFolder : undefined}
              onMoveFolder={canEdit ? handleMoveFolder : undefined}
              onDeleteFolder={canEdit ? handleDeleteFolder : undefined}
              shareAdapter={shareAdapter}
              onCopyLink={handleCopyLink}
              showAudioInspector
              // 埋め込み時は ProjectWrapper の BottomAppBar（80px）ぶん持ち上げる
              audioInspectorBottomOffset={embedded ? 80 : 0}
              onAudioInspectorFileChange={setInspectorFileId}
              audioBookmarks={audioBookmarks}
              onCreateAudioBookmark={handleCreateAudioBookmark}
              onUpdateAudioBookmark={handleUpdateAudioBookmark}
              onDeleteAudioBookmark={handleDeleteAudioBookmark}
              audioFocusRequest={audioJump}
              onAudioFocusRequestApplied={handleAudioFocusApplied}
            />
          )}
        </Content>
      </ContentArea>

      {/* アップロード進捗モーダル（トークルーム / DM と同じ UI） */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            // アップロード中と登録中はモーダルを閉じない
            const stillUploading = uploadingFiles.some(
              (f) => f.status === "uploading" || f.status === "pending",
            );
            if (!stillUploading) clearCompleted();
          }}
          size="default"
        >
          <UploadModalTitle>ファイルアップロード</UploadModalTitle>
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={abortFile}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}

      {sectionModal.open && (
        <LibrarySectionSettingsModal
          section={sectionModal.section}
          canManage={canManage}
          onClose={() => setSectionModal({ open: false })}
        />
      )}
    </PageWrapper>
  );
};

export default Library;
